import { ChangeEvent, MutableRefObject, useState } from "react";
import { Combination, Result } from "../../types";
import { Column, RowsChangeData, textEditor } from "react-data-grid";
import { exportExcel, readExcel } from "../../services";
import _ from "lodash";
import { calculateFactor, calculatePallets } from "../../utils";

// Define columns for the DataGrid
const columns: readonly Column<Combination>[] = [
    { key: "supplier", name: "Supplier", renderEditCell: textEditor },
    { key: "supplierPort", name: "Supplier Port", renderEditCell: textEditor },
    { key: "service", name: "Service", renderEditCell: textEditor },
    { key: "line", name: "Shipping Line", renderEditCell: textEditor },
    { key: "arrival", name: "Week of Arrival", renderEditCell: textEditor },
    { key: "rate", name: "Rate Type", renderEditCell: textEditor },
    { key: "port", name: "Port", renderEditCell: textEditor },
    { key: "warehouse", name: "WH", renderEditCell: textEditor },
    { key: "item", name: "Item", renderEditCell: textEditor },
    { key: "quantity", name: "Quantity to be Arrived", renderEditCell: textEditor },
    { key: "pallets", name: "# of Pallets to be Arrived", renderEditCell: textEditor },
    { key: "availablePallets", name: "Available Pallets Capacity", renderEditCell: textEditor },
    { key: "variable", name: "Variable", renderEditCell: textEditor },
    { key: "factor", name: "X-Factor", renderEditCell: textEditor },
    { key: "forecast", name: "Forecast", renderEditCell: textEditor },
    { key: "quarterlyAvgForecast", name: "Quarterly Avg. Forecast", renderEditCell: textEditor },
    { key: "soh", name: "SOH", renderEditCell: textEditor },
    { key: "palletCapacity", name: "Pallet Capacity", renderEditCell: textEditor },
    { key: "palletHoldingCost", name: "Pallet Holding Cost", renderEditCell: textEditor },
    { key: "docMax", name: "Maximum DOC", renderEditCell: textEditor },
    { key: "inboundCost", name: "Inbound Cost", renderEditCell: textEditor },
    { key: "outboundCost", name: "Outbound Cost", renderEditCell: textEditor },
    { key: "caseMargin", name: "Case Margin", renderEditCell: textEditor },
];

const useCombinationsLogic = () => {
    // Initialize Combinations as an empty array
    const [combinations, setCombinations] = useState<Record<string, Combination>>({});

    const [output1, setOutput1] = useState<string>("");
    const [output2, setOutput2] = useState<string>("");
    const [output3, setOutput3] = useState<string>("");

    const [importLoading, setImportLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);

    const [result, setResult] = useState<Result>({
        optimal_values: "",
        message: "",
        optimal_value: 0,
    });

    const [isFormulationsHidden, setIsFormulationsHidden] = useState(true);

    const generateOutputs = async () => {
        try {
            setExportLoading(true);
            const combinationsArr = Object.values(combinations);
            const newOutput1 = combinationsArr.reduce((prev, curr) => {
                if (prev === "") return `${curr.factor}*${curr.variable}`;
                return `${prev} + ${curr.factor}*${curr.variable}`;
            }, "");
            const newOutput2 = combinationsArr.reduce((prev, curr) => {
                if (prev === "") return `${curr.variable}`;
                return `${prev} + ${curr.variable}`;
            }, "");
            const newOutput3 = combinationsArr.reduce((prev, curr) => {
                if (prev === "") return `${curr.pallets}*${curr.variable} <= ${curr.availablePallets}`;
                return `${prev}\n${curr.pallets}*${curr.variable} <= ${curr.availablePallets}`;
            }, "");

            setOutput1(newOutput1);
            setOutput2(`${newOutput2} = 1`);
            setOutput3(newOutput3);

            //solver from env
            // const res = await fetch(process.env.REACT_APP_SOLVER_URL ?? "https://solver.muqassim.com/solve_one", {
            const res = await fetch("https://solver.muqassim.com/solve_one", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    // Send the LP problem data here
                    // Replace this with your specific data format
                    rows: combinationsArr,
                }),
            });

            const data = await res.json();

            const newCombinations = _.cloneDeep(combinations);
            // {x1: 1, x2: 0, x3: 0, ...}
            const xValues: { [key: string]: number } = data.values;
            let optimalValue = 0;
            Object.keys(xValues).forEach((key) => {
                if (xValues[key] === 1) {
                    optimalValue = data.optimal_value;
                }
                newCombinations[key].variableValue = xValues[key];
            });

            exportExcel({
                combinations: Object.values(newCombinations),
                optimalValue: optimalValue,
            });
            setCombinations(newCombinations);
            setResult({
                ...data,
                optimal_value: optimalValue,
            });
        } catch (error) {
            console.error(error);
        }
        setExportLoading(false);
    };

    const onRowsChange = (updatedRows: Combination[], data: RowsChangeData<Combination, unknown>) => {
        const index = data.indexes[0];
        const newUpdatedRows = _.cloneDeep(updatedRows);
        let updatedRow = newUpdatedRows[index];
        // handle nunber input
        if (
            [
                "arrival",
                "quantity",
                "pallets",
                "availablePallets",
                "factor",
                "forecast",
                "quarterlyAvgForecast",
                "soh",
                "palletCapacity",
                "palletHoldingCost",
                "docMax",
                "inboundCost",
                "outboundCost",
                "caseMargin",
            ].includes(data.column.key)
        ) {
            const key = data.column.key as keyof Combination;
            updatedRow = { ...updatedRow, [key]: Number(updatedRow[key]) ?? 0 };
            updatedRow.factor = calculateFactor(updatedRow);
            updatedRow.pallets = calculatePallets(updatedRow);
        }
        newUpdatedRows[index] = { ...updatedRow, variableValue: null };

        const newCombinations = newUpdatedRows.reduce((prev, curr) => ({ ...prev, [curr.variable]: curr }), {});
        setCombinations(newCombinations);
    };

    const handleFileChange = async (e: ChangeEvent<HTMLInputElement>, ref: MutableRefObject<HTMLInputElement>) => {
        if (e.target === null || e.target.files === null || ref === null) return;
        try {
            setImportLoading(true);
            const file = e.target.files[0];

            if (file) {
                const data = await readExcel(file);
                const newCombinations = data.reduce((prev: { [key: string]: Combination }, curr: Combination) => {
                    curr.factor = calculateFactor(curr);
                    curr.pallets = calculatePallets(curr);
                    prev[curr.variable] = curr;
                    return prev;
                }, {});
                setCombinations(newCombinations);
                // Clear the input value
                if (ref.current) {
                    ref.current.value = "";
                }
            }
        } catch (error) {
            console.error(error);
        }
        setImportLoading(false);
    };

    return {
        //vars
        columns,
        //states
        combinations,

        output1,
        output2,
        output3,
        result,
        isFormulationsHidden,

        //loading
        importLoading,
        exportLoading,

        //functions
        generateOutputs,
        onRowsChange,
        handleFileChange,
        setIsFormulationsHidden,
    };
};

export default useCombinationsLogic;
